import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, ArrowLeft } from 'lucide-react';
import EventsList from './EventsList';
import EventForm from './EventForm';
import PondingLogForm from './PondingLogForm';
import Modal from './Modal';
import { Event, PondingLog } from './types';

export default function FloodEventsManager() {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [refreshEvents, setRefreshEvents] = useState(0);
  
  // Ponding log management
  const [showPondingModal, setShowPondingModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [editingPonding, setEditingPonding] = useState<PondingLog | null>(null);

  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEvent(null);
    setRefreshEvents(prev => prev + 1);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleAddPonding = (eventId: string) => {
    setSelectedEventId(eventId);
    setEditingPonding(null);
    setShowPondingModal(true);
  };

  const handleEditPonding = (ponding: PondingLog) => {
    setSelectedEventId(ponding.eventId);
    setEditingPonding(ponding);
    setShowPondingModal(true);
  };

  const handlePondingSuccess = () => {
    setShowPondingModal(false);
    setSelectedEventId(null);
    setEditingPonding(null);
    setRefreshEvents(prev => prev + 1);
  };

  const handlePondingCancel = () => {
    setShowPondingModal(false);
    setSelectedEventId(null);
    setEditingPonding(null);
  };

  return (
    <div className="h-screen flex flex-col bg-background p-6">
      <div className="px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              {showForm ? (editingEvent ? 'Edit Flood Event' : 'Add Flood Event') : 'Flood Events'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {showForm ? (editingEvent ? 'Update event details' : 'Create a new flood event') : 'View and manage flood events and ponding logs'}
            </p>
          </div>
          {showForm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleFormCancel}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Events
            </Button>
          ) : (
            <Button
              onClick={handleAddEvent}
              size="sm"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Event
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1">
        {showForm ? (
          <div className="px-6 py-4">
            <EventForm
              editingEvent={editingEvent}
              onSuccess={handleFormClose}
              onCancel={handleFormCancel}
            />
          </div>
        ) : (
          <div className="px-6 py-4">
            <EventsList 
              onEdit={handleEditEvent} 
              onAddPonding={handleAddPonding}
              onEditPonding={handleEditPonding}
              key={refreshEvents} 
            />
          </div>
        )}
      </div>

      {/* Ponding Log Modal */}
      {showPondingModal && selectedEventId && (
        <Modal
          isOpen={showPondingModal}
          onClose={handlePondingCancel}
          title={editingPonding ? 'Edit Ponding Log' : 'Add Ponding Log'}
        >
          <PondingLogForm
            eventId={selectedEventId}
            editingLog={editingPonding}
            onSuccess={handlePondingSuccess}
            onCancel={handlePondingCancel}
          />
        </Modal>
      )}
    </div>
  );
}
